import { z } from 'zod';

import { pageMetaSchema } from '@/lib/api/pagination';

/** The admin-review state of a Warranty Claim (CONTEXT.md). `pending` is the only actionable
 *  state; `approved`/`rejected` are final. Distinct from `warrantyStatusSchema`. */
export const claimStatusSchema = z.enum(['pending', 'approved', 'rejected']);

/** The *device's* warranty-coverage state (CONTEXT.md) — informational on this queue, never
 *  actioned. A different field from Claim Status; do not conflate the two.
 *
 *  Tokens are `active | expired`: the backend **computes** this from the order's `deliveredAt` plus
 *  the item's `warrantyMonths` (`computeWarrantyStatus`), and no `inactive` token exists anywhere in
 *  the contract. The earlier `inactive` guess predated the deployed endpoint. */
export const warrantyStatusSchema = z.enum(['active', 'expired']);

/** The five fixed customer-selected claim reasons (story 110178). Wire tokens are snake_case;
 *  display labels live in `format.ts`. */
export const warrantyReasonSchema = z.enum([
  'mechanical_or_electrical_failure',
  'manufacturing_defects',
  'performance_failure',
  'structural_flaws',
  'others',
]);

/** One row = one claim = one device (grilling Q16) — NOT order-grouped like Installations. The
 *  twelve fields of `WarrantyClaimGlobalListItem`; `orderNumber` is the `ORD-XXXXXXXXXX` display
 *  value (an opaque string, never routed on).
 *
 *  `orderId` is **nullish** deliberately. The "Order Details → View" column routes on the internal
 *  id, but `mapToListItem` does not send one — BE was asked for the one-line addition on 110178
 *  (`wc.order` is already loaded, `orderId` is an indexed column). Typing it tolerant means the
 *  column lights up the day the field appears, with no further FE change; the page renders the View
 *  link only when it is present. The detail response *does* carry `orderId`, so the drawer's own
 *  order links work today. Same bridge as `quantity`/FLAG-5. */
export const warrantyClaimRowSchema = z.object({
  id: z.string(),
  orderId: z.string().nullish(),
  orderNumber: z.string(),
  customerName: z.string(),
  phone: z.string(),
  orderDate: z.string(),
  city: z.string(),
  claimStatus: claimStatusSchema,
  deviceCategory: z.string().nullable(),
  deviceName: z.string(),
  warrantyStatus: warrantyStatusSchema,
  reason: warrantyReasonSchema,
  claimDate: z.string(),
});

export const warrantyClaimListResponseSchema = z.object({
  data: z.array(warrantyClaimRowSchema),
  meta: pageMetaSchema,
});

/** A customer-uploaded evidence image on a claim (`WarrantyClaimImageDetail`; named for the wire's
 *  `images[]`, matching `replacementImageSchema` — the drawer still *calls* them attachments in its
 *  copy). Read-only to the admin — the drawer only displays these (never re-uploads), so this is not
 *  the `<Upload>` value shape (grilling Q7). `url` is nullable (an unresolvable storage key), so the
 *  gallery skips those tiles; `sortOrder` is parsed but unused — the array already arrives ordered. */
const warrantyClaimImageSchema = z.object({
  id: z.string(),
  url: z.string().nullable(),
  originalFileName: z.string().nullable(),
  sortOrder: z.number(),
});

/** One Activity Timeline event as the **wire** names it: `timeline: [{status, timestamp}]`, from the
 *  backend's `buildTimeline`. `status` stays an un-narrowed string because BE types it that way too
 *  — a token they add later reaches the drawer (which degrades it to a neutral chip) instead of
 *  failing the parse. Same call the order-scoped sibling made on 112445. */
const warrantyTimelineEntrySchema = z.object({
  status: z.string(),
  timestamp: z.string(),
});

/** An internal user credited on the claim (`AdminSummary`) — who raised it on the customer's behalf,
 *  and who decided it. */
const warrantyAdminSummarySchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});

/** Full warranty-claim detail (`WarrantyClaimDetailResponse`) — **item-centric, not the row plus
 *  extras**. It carries no `customerName`, `phone`, `city`, `orderDate` — and no `address` at all —
 *  so the queue page merges it with the row it already fetched, exactly as 114698 did for the
 *  Replacements sibling. (`address` has no source anywhere in this contract; the drawer shows the
 *  row's City in its place.)
 *
 *  `timeline` is `nullish`-collapsed to `[]` so a deployment predating the field renders no timeline
 *  rather than failing the parse. `reasonLabel` is parsed but unused — `WARRANTY_REASONS[reason]`
 *  keeps one casing for the queue and its filter options. */
export const warrantyClaimDetailSchema = z.object({
  id: z.string(),
  itemName: z.string(),
  bundleName: z.string().nullable(),
  category: z.string().nullable(),
  imageUrl: z.string().nullable(),
  unitIndex: z.number(),
  quantity: z.number(),
  bundleDeviceId: z.string().nullable(),
  reason: warrantyReasonSchema,
  reasonLabel: z.string(),
  description: z.string().nullable(),
  status: claimStatusSchema,
  requestedAt: z.string(),
  requestedBy: warrantyAdminSummarySchema.nullable(),
  resolvedAt: z.string().nullable(),
  resolvedBy: warrantyAdminSummarySchema.nullable(),
  images: z
    .array(warrantyClaimImageSchema)
    .nullish()
    .transform((value) => value ?? []),
  rejectionReason: z.string().nullish(),
  orderId: z.string(),
  orderNumber: z.string(),
  warrantyStatus: warrantyStatusSchema,
  timeline: z
    .array(warrantyTimelineEntrySchema)
    .nullish()
    .transform((value) => value ?? []),
});

/** Reject-modal form validation (ticket 04 AC3/AC4). The empty-reason message is AC-verbatim; the
 *  2000-character rule is a belt-and-braces guard — the textarea hard-caps typing at the limit, so
 *  the max message should never reach the UI (AC4: no over-limit error). */
export const rejectWarrantyClaimSchema = z.object({
  reason: z
    .string()
    .max(2000, 'Reason must be at most 2000 characters')
    .refine((s) => s.trim().length > 0, { message: 'Please enter a reason for rejection' }),
});

/** Filter-drawer draft validation. Multi-selects are arrays; each date bound is a nullable
 *  `YYYY-MM-DD` string. Both ranges must satisfy From <= To, with the AC's verbatim copy
 *  attached to the `*To` path so the field renders the error and Apply can disable.
 *  (`enforceRangePairs` is number-only, so this is hand-written — same as
 *  `installationFiltersSchema`.) */
export const warrantyFiltersSchema = z
  .object({
    deviceIds: z.array(z.string()),
    categoryIds: z.array(z.string()),
    cities: z.array(z.string()),
    warrantyStatuses: z.array(warrantyStatusSchema),
    claimStatuses: z.array(claimStatusSchema),
    reasons: z.array(warrantyReasonSchema),
    orderDateFrom: z.string().nullable(),
    orderDateTo: z.string().nullable(),
    claimDateFrom: z.string().nullable(),
    claimDateTo: z.string().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.orderDateFrom && v.orderDateTo && v.orderDateFrom > v.orderDateTo) {
      ctx.addIssue({
        code: 'custom',
        path: ['orderDateTo'],
        message: 'End date must be after start date',
      });
    }
    if (v.claimDateFrom && v.claimDateTo && v.claimDateFrom > v.claimDateTo) {
      ctx.addIssue({
        code: 'custom',
        path: ['claimDateTo'],
        message: 'End date must be after start date',
      });
    }
  });

/** One cursor page of the Device filter's typeahead. Mirrors `replacementOptionPageSchema` — the
 *  two queues call the same `/products/admin/meta/devices` endpoint with the same contract. */
export const warrantyDeviceOptionSchema = z.object({ id: z.string(), name: z.string() });

export const warrantyDeviceOptionPageSchema = z.object({
  data: z.array(warrantyDeviceOptionSchema),
  meta: z.object({ nextCursor: z.string().optional(), hasNextPage: z.boolean() }),
});
